import { CategorizedImage } from 'src/utils/hotelFilter';
import { Rate } from 'src/utils/hotelFilters';
import { CommissionWithBreakdownType, compareRatesForTraveler, getTravelerChargeTotal } from 'src/utils/pricing.util';

/**
 * Youth-sports season planner — choosing the three rooms a parent sees.
 *
 * The whole design goal is less thinking: a parent opens the team hotel, sees
 * three rooms, and books one. So the three are chosen for them rather than
 * being the first three the supplier happened to return.
 *
 * The rules, from Daniel (2026-08-30):
 *  - three cards;
 *  - refundable rates are the default WHERE AVAILABLE;
 *  - one or two rooms that sleep several, at most one single-bed room;
 *  - a room with one bed plus a sofa bed or pullout counts as two beds.
 *
 * That last rule is why bed counting cannot just read the bed type: "1 King
 * Bed and Sofa Bed" is a family room, and a family of four booking it as a
 * "one bed" room is the mistake this is here to prevent.
 */

export interface PlannerRoom extends Rate {
   roomName?: string;
   freeCancellation?: boolean;
   remainingRooms?: number;
   // The same commission shape the pricing helpers take, so the per-night
   // figure here is always the one the traveler is shown.
   commission?: CommissionWithBreakdownType;
   /** Identifies the rate; `blockId` rotates between search sessions, `roomId` does not. */
   roomId?: string | null;
   blockId?: string | null;
   /** The physical room this rate is for — what groups rate variants together. */
   parentRoomId?: string | number | null;
   /** One entry per night: what turns a nightly rate into a stay total. */
   dailyRate?: unknown[];
   totalPayment?: { tax?: number };
   /** Added client-side from the hotel's static record — the rates call has neither. */
   images?: unknown[];
   categorizedImages?: CategorizedImage[];
   /** The other rate plans for the same physical room (meal / cancellation choices). */
   options?: PlannerRoom[];
}

/** Sofa beds, pullouts and murphy beds sleep people, so they count. */
const EXTRA_BED_PATTERN = /(sofa|pull[- ]?out|pullout|sleeper|futon|murphy|day ?bed|trundle|rollaway)/i;

/** Any word that means "there is a bed here". */
// Whole words only (NPT-2232): "Smoking" contains "king", and "Non Smoking" ends most supplier room names.
const BED_WORD_PATTERN = /\b(kings?|queens?|doubles?|full|twins?|singles?|bunks?|beds?)\b/i;

/**
 * "2 Queen Beds", "Two Doubles", "3 twin beds", "2 Queens".
 *
 * The bed word is what carries the count, and the literal word "bed" is
 * optional — suppliers write "2 Queens" as often as "2 Queen Beds".
 */
const BED_QUANTITY_PATTERN = /\b(\d+|two|three|four)\s+(?:[a-z]+\s+)?(king|queen|double|full|twin|single|bunk|bed)s?\b/gi;

const WORD_NUMBERS: Record<string, number> = { two: 2, three: 3, four: 4 };

/** A named bed that is not a sofa, pullout or similar: what "a bed plus a sofa bed" needs on the bed side. */
const PRIMARY_BED_WORD_PATTERN = /\b(kings?|queens?|doubles?|full|twins?|singles?|bunks?)\b/i;

/**
 * Sleeping surfaces named in one phrase.
 *
 * Quantities carry the count ("2 Queen Beds" is two). A phrase with no quantity but a bed
 * word in it is one bed: "King Room" is a bed, and reading it as zero is what made "King
 * Room with Sofa Bed" come back as a single-bed room. A sofa bed or pullout named beside the
 * beds adds one ("1 King Bed with Sofa Bed" and "King Room with Sofa Bed" are two) unless it
 * was already counted as a quantity of its own ("1 Sofa Bed").
 *
 * NPT-2026 — the same reading as the API's `countInPhrase` (`utils/hotel-bed-types.util.ts`),
 * which decides the policy's `bed` term and the `multiBed` badge; the two must agree or the
 * badge contradicts the filter. Until NPT-2026 a sofa bed in the SAME phrase as a quantity
 * was dropped here ("1 King Bed with Sofa Bed" read as one).
 */
function countInPhrase(text: string): number {
   let total = 0;
   let extraCounted = false;
   const quantities = new RegExp(BED_QUANTITY_PATTERN);
   let match: RegExpExecArray | null;
   while ((match = quantities.exec(text)) !== null) {
      const raw = match[1].toLowerCase();
      // `Number(raw) ?? 0` never falls back — Number() returns NaN, not null,
      // so a non-numeric match would have made the whole total NaN. eslint's
      // no-constant-binary-expression caught it; the fallback is real now.
      const parsed = WORD_NUMBERS[raw] ?? Number.parseInt(raw, 10);
      total += Number.isFinite(parsed) ? parsed : 0;
      if (EXTRA_BED_PATTERN.test(match[0])) extraCounted = true;
   }
   const hasExtra = EXTRA_BED_PATTERN.test(text);
   if (total > 0) return hasExtra && !extraCounted ? total + 1 : total;
   const primary = PRIMARY_BED_WORD_PATTERN.test(text) ? 1 : 0;
   if (primary || hasExtra) return primary + (hasExtra ? 1 : 0);
   return BED_WORD_PATTERN.test(text) ? 1 : 0;
}

/**
 * How many sleeping surfaces a room offers, from whatever the supplier gave us:
 * the structured `beddings` / `bedType` fields first, then the room name.
 *
 * Returns null when there is nothing to read — the caller treats that as
 * "unknown" rather than as "one bed", because guessing low would push a family
 * into a room that cannot hold them.
 */
/**
 * How many sleeping surfaces a room offers, from whatever the supplier gave us:
 * the structured `beddings` / `bedType` fields first, then the room name.
 *
 * Returns null when there is nothing to read — the caller treats that as
 * "unknown" rather than as "one bed", because guessing low would push a family
 * into a room that cannot hold them.
 */
export function countBeds(room: PlannerRoom | null | undefined): number | null {
   if (!room) return null;

   const structured = room.beddings ?? room.bedType ?? undefined;
   const textParts = [
      Array.isArray(room.beddings) ? room.beddings.join(' and ') : room.beddings,
      room.bedType,
      room.roomName,
   ]
      .filter(Boolean)
      .map(String);

   if (!textParts.length) return null;

   const text = textParts.join(' and ');
   if (!text.trim()) return null;

   // Extract raw phrase segments from structured bedding fields
   const rawPhrases: string[] = [];
   if (Array.isArray(structured)) {
      structured.forEach(item => {
         if (item) String(item).split(/;|\+|&|and|,/i).forEach(p => {
            const trimmed = p.trim();
            if (trimmed) rawPhrases.push(trimmed);
         });
      });
   } else if (typeof structured === 'string' && structured.trim()) {
      structured.split(/;|\+|&|and|,/i).forEach(p => {
         const trimmed = p.trim();
         if (trimmed) rawPhrases.push(trimmed);
      });
   }

   let count = 0;
   if (rawPhrases.length > 0) {
      count = rawPhrases.reduce((sum, phrase) => sum + countInPhrase(phrase), 0);
   }

   // Fallback to text parsing if structured phrases yielded no beds
   if (count === 0) {
      count = countInPhrase(text);
   }

   // A sofa bed or pullout named only in the room name ("King Suite with Sofa Bed" over a
   // bedding of "1 King Bed") still adds capacity. countInPhrase already counts an extra named
   // in the same phrase, so this only runs when the structured fields missed it.
   const extraInText = EXTRA_BED_PATTERN.test(text);
   const extraInPhrases = rawPhrases.some(p => EXTRA_BED_PATTERN.test(p));
   if (rawPhrases.length > 0 && extraInText && !extraInPhrases && count > 0) {
      count = Math.max(count, countInPhrase(text));
   }

   // Fallback for Family, Triple, Quad room names when no explicit bed counts found
   if (count === 0) {
      const lower = text.toLowerCase();
      if (/\b(quad|quadruple)\b/i.test(lower)) count = 4;
      else if (/\b(triple)\b/i.test(lower)) count = 3;
      else if (/\b(family)\b/i.test(lower)) count = 2;
   }

   return count > 0 ? count : null;
}

/** A room a family can share: two or more sleeping surfaces. */
export const sleepsSeveral = (room: PlannerRoom): boolean => (countBeds(room) ?? 0) >= 2;

/**
 * Does this PROPERTY have a room that sleeps several — two or more beds, or a bed plus a
 * sofa bed (ADR §3, NPT-2026)? The web's copy of the API's `multiBedFromBedTypes`, for
 * cache entries and static rows that predate the API's own `multiBed` field.
 *
 * Deliberately not asked of the rates on the results page (NPT-1734). That list carries
 * exactly one rate per hotel — BH caps ratesPerProperty at 1 for the 31-100 HID batches
 * the search sends — so judging the property by it hides any hotel whose cheapest room
 * happens to be a king. `bedTypes` is the property's static room-type catalogue, which
 * answers the question the traveler is actually asking: does this hotel HAVE such a room.
 *
 * Null when the catalogue is empty, missing or unreadable: the supplier told us nothing,
 * which is not "king-only". The badge leaves null blank, the policy's fit term scores it
 * neutral, and the "Two beds" filter drops it only while the traveler has the filter on
 * (`passesTwoBedsFilter`). It is never an exclusion on its own.
 */
export function multiBedFromBedTypes(bedTypes: unknown): boolean | null {
   if (!Array.isArray(bedTypes) || bedTypes.length === 0) return null;

   const readable = bedTypes.map(bedType => countAlternatives(String(bedType ?? ''))).filter((n): n is number => n != null);
   if (readable.length === 0) return null;

   return readable.some(n => n >= 2);
}

/**
 * Beds in one catalogue entry, where "or" offers a CHOICE of layouts.
 *
 * Suppliers write a convertible room as "1 king bed or 2 twin beds": the room is made up one
 * way or the other, never both. countBeds sums what it sees — right for "and", wrong here, and
 * wrong in the direction that matters: "1 king bed or 1 sofa bed" sums to 2 and would pass a
 * 2-bed filter on a room with one sleeping surface whichever way it is made up.
 *
 * So each alternative is counted on its own and the best one wins. Kept here rather than pushed
 * into countBeds because that function also feeds the planner's room cards, and this is a
 * catalogue-string concern.
 */
function countAlternatives(bedType: string): number | null {
   const counts = bedType
      .split(/\bor\b/i)
      .map(alternative => countBeds({ bedType: alternative.trim() }))
      .filter((n): n is number => n != null);

   return counts.length ? Math.max(...counts) : null;
}

/** The refundable rate wins the default when it costs less than this much more per night. */
const REFUNDABLE_PREMIUM_PER_NIGHT = 25;

/**
 * What the traveler pays per night for this rate, the whole booking (every room), tax included.
 * The same charge `pickBestRate` ranks on, spread over the stay so the refundable premium above can
 * be a per-night amount. Infinity when the rate has no price.
 */
const chargePerNight = (room: PlannerRoom): number => {
   const charge = getTravelerChargeTotal(room);
   if (charge === undefined) return Number.POSITIVE_INFINITY;
   return charge / Math.max(1, room.dailyRate?.length ?? 1);
};

const isPriced = (room: PlannerRoom): boolean => getTravelerChargeTotal(room) !== undefined;

/**
 * Prefer the refundable version of the same room.
 *
 * Suppliers list the same room twice — once non-refundable and cheaper, once
 * refundable. Showing both wastes one of only three cards on a duplicate, and
 * a youth-sports trip is exactly the booking most likely to be cancelled.
 * Between two copies that are equally refundable, the shared rate rule
 * (`compareRatesForTraveler`: lowest charge, then highest proceeds) decides.
 */
function preferRefundable(rooms: PlannerRoom[]): PlannerRoom[] {
   const byName = new Map<string, PlannerRoom>();
   for (const room of rooms) {
      const key = `${(room.roomName ?? '').toLowerCase().trim()}|${countBeds(room) ?? '?'}`;
      const held = byName.get(key);
      if (!held) {
         byName.set(key, room);
         continue;
      }
      const heldRefundable = held.freeCancellation === true;
      const roomRefundable = room.freeCancellation === true;
      if (roomRefundable && !heldRefundable) byName.set(key, room);
      else if (roomRefundable === heldRefundable && compareRatesForTraveler(room, held) < 0) byName.set(key, room);
   }
   return Array.from(byName.values());
}

/**
 * Card order: the shared rate rule (`compareRatesForTraveler`), except that a refundable rate goes
 * first when it costs less than REFUNDABLE_PREMIUM_PER_NIGHT more per night, so the default a parent
 * sees is the one they can cancel.
 */
export function comparePlannerRooms(a: PlannerRoom, b: PlannerRoom): number {
   const refundable = Number(b.freeCancellation === true) - Number(a.freeCancellation === true);
   if (refundable !== 0 && Math.abs(chargePerNight(a) - chargePerNight(b)) < REFUNDABLE_PREMIUM_PER_NIGHT) return refundable;
   return compareRatesForTraveler(a, b);
}

export interface PlannerRoomSelection {
   rooms: PlannerRoom[];
   /** True when at least one card is a refundable rate. */
   hasRefundable: boolean;
}

/**
 * Pick the three rooms to show, per the rules above.
 *
 * Order within the three is cheapest-first among the multi-bed rooms, then the
 * single-bed ones — a parent booking for a family should land on a room that
 * fits them without reading. Highlight 2-bed options first; if no multi-bed rooms
 * are found, return the top 3 best available rooms.
 */
export function pickPlannerRooms(allRooms: PlannerRoom[] | null | undefined, limit = 3): PlannerRoomSelection {
   const rooms = preferRefundable((allRooms ?? []).filter(isPriced));
   if (!rooms.length) return { rooms: [], hasRefundable: false };

   const cheapestFirst = [...rooms].sort(comparePlannerRooms);

   const multiBed = cheapestFirst.filter(sleepsSeveral);
   const singleBed = cheapestFirst.filter(room => !sleepsSeveral(room));

   const picked: PlannerRoom[] = [];
   
   // 1. Prioritize multi-bed rooms (2+ beds) up to limit
   const multiBedTarget = Math.min(multiBed.length, limit);
   picked.push(...multiBed.slice(0, multiBedTarget));

   // 2. If fewer than limit multi-bed rooms exist, top up with single-bed rooms
   if (picked.length < limit && singleBed.length) {
      for (const room of singleBed) {
         if (picked.length >= limit) break;
         if (!picked.includes(room)) picked.push(room);
      }
   }

   // 3. Top up with any remaining multi-bed rooms if any were skipped
   if (picked.length < limit) {
      for (const room of multiBed) {
         if (picked.length >= limit) break;
         if (!picked.includes(room)) picked.push(room);
      }
   }

   return { rooms: picked.slice(0, limit), hasRefundable: picked.some(room => room.freeCancellation === true) };
}

/** Room pictures arrive as bare URLs or as objects, and the CDN default is too small for a card. */
export function firstRoomImage(images: unknown[] | undefined): string {
   const first = images?.[0] as { url?: string; image_url?: string; picture_url?: string } | string | undefined;
   if (!first) return '';
   const url = typeof first === 'string' ? first : (first.url ?? first.image_url ?? first.picture_url ?? '');
   return url ? url.replace('{size}', 'x500').replace(/s=312x/gi, 's=640x400') : '';
}
